import axiosInstance from "@/api/axios-instance";

export interface AuditRun {
  id: string;
  type: string;
  status: "PENDING" | "RUNNING" | "STOPPING" | "STOPPED" | "COMPLETED" | "FAILED";
  triggered_by: string;
  filters: { date_from?: string | null; date_to?: string | null; exclude?: string | null } | null;
  total_groups: number | null;
  processed_groups: number;
  mapped_sessions: number;
  skipped_sessions: number;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuditLogEntry {
  id: string;
  audit_run_id: string;
  identity_key: string;
  invitee_name: string | null;
  session_count: number;
  mapped_count: number;
  skipped_count: number;
  mappings: Array<{
    session_id: string;
    session_name: string;
    session_start_time?: string;
    bundle_usage_id: string;
    bundle_name: string;
  }> | null;
  skipped: Array<{
    session_id: string;
    session_name: string;
    session_start_time?: string;
    reason: string;
  }> | null;
  created_at: string;
}

export interface AuditRunResponse {
  run: AuditRun;
  logs: AuditLogEntry[];
  log_total: number;
  log_page: number;
  log_last_page: number;
}

export interface StartAutoFixParams {
  date_from?: string;
  date_to?: string;
  exclude?: string;
}

export async function startAutoFix(params: StartAutoFixParams): Promise<{ run_id: string }> {
  const res = await axiosInstance.post("/audit/unmapped-sessions/auto-fix/start", params);
  return res.data;
}

export async function stopAutoFix(runId: string): Promise<{ success: boolean }> {
  const res = await axiosInstance.patch(`/audit/unmapped-sessions/auto-fix/${runId}/stop`);
  return res.data;
}

export async function getActiveAutoFixRun(): Promise<{ run: AuditRun | null }> {
  const res = await axiosInstance.get("/audit/unmapped-sessions/auto-fix/active");
  return res.data;
}

export async function getAutoFixRun(
  runId: string,
  logsPage = 0,
  logsLimit = 20
): Promise<AuditRunResponse> {
  const res = await axiosInstance.get(`/audit/unmapped-sessions/auto-fix/${runId}`, {
    params: { logs_page: logsPage, logs_limit: logsLimit },
  });
  return res.data;
}

export interface ListAutoFixRunsResponse {
  runs: AuditRun[];
  total: number;
  page: number;
  last_page: number;
}

export async function listAutoFixRuns(
  page = 0,
  limit = 20
): Promise<ListAutoFixRunsResponse> {
  const res = await axiosInstance.get("/audit/unmapped-sessions/auto-fix/list", {
    params: { page, limit },
  });
  return res.data;
}

export async function downloadAutoFixExport(runId: string): Promise<void> {
  const res = await axiosInstance.get(
    `/audit/unmapped-sessions/auto-fix/${runId}/export`,
    { responseType: "blob" }
  );
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const disposition = res.headers["content-disposition"] as string | undefined;
  const match = disposition?.match(/filename="(.+?)"/);
  const filename = match?.[1] ?? `audit-fix-${runId.slice(0, 8)}.csv`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
